
import { computed, defineComponent, onMounted, PropType, reactive, ref } from 'vue';
import {
  ImageItem,
  PrmRelationship,
  PrmSearchResult,
  Table,
  TableRow,
} from '../services/swagger/index.defs';

import { GetPrmListCommand } from '@/products/api/runtime/CommandExecutor';

import Tooltip from './Tooltip.vue';
import PisLink from './PisLink.vue';
import Loader from './Loader.vue';
import Icon from './Icon.vue';
import Pagination from './Pagination.vue';
import ImageGallery from './ImageGallery.vue';
import FormInputClearable from '@/common/components/FormInputClearable.vue';
import Dropdown from '@/common/components/Dropdown.vue';
import Clickable from '@/common/components/Clickable.vue';
import { TranslateFunc } from '@/products/composables/setupComponent';
import _debounce from 'lodash/debounce';
import { IInstance } from '../api/runtime/IInstance';
import { Instance } from '@/products/api/Instance';
export default defineComponent({
  name: 'PrmList',

  components: {
    Tooltip,
    PisLink,
    Icon,
    Pagination,
    ImageGallery,
    FormInputClearable,
    Loader,
    Dropdown,
    Clickable,
  },

  props: {
    relationship: { type: Object as () => PrmRelationship, required: true },
    instance: { type: Object as () => IInstance | undefined, required: true },
    productId: { type: String, required: true },
    t: { type: Function as PropType<TranslateFunc>, required: true },
  },

  setup(props) {
    const prm = computed(() => props.relationship as PrmRelationship);
    const table = computed(() => prm.value.table as Table);

    const filter = ref<string | undefined>(undefined);
    const page = ref(1);
    const pageSize = ref(5); // TODO: from settings
    const totalCount = ref(table.value.rowsTotalCount || 0);
    const loadedRows = ref<TableRow[]>([]);
    const isSearched = ref(false);
    const productsImages = ref<ImageItem[]>([]);
    const productsImagesLoading = ref(false);
    const isLoading = ref(false);
    const isAutocompleteVisible = ref(false);
    const isAutocompleteLoading = ref(false);
    const isAutocompleteDisabled = ref(false);

    const rows = computed(() => {
      if (isSearched.value === true) {
        return loadedRows.value;
      }
      return props.relationship.table?.rows || [];
    });

    const pageCount = computed(() => {
      return Math.ceil(totalCount.value / pageSize.value);
    });

    const autocomplete = reactive({
      visibleMatchCount: 0,
      totalCount: 0,
      matches: [],
    });

    const load = async () => {
      isLoading.value = true;
      const command = new GetPrmListCommand({
        page: page.value,
        pageSize: pageSize.value,
        productId: props.productId,
        filter: filter.value || undefined,
        relationshipType: props.relationship.type,
      });

      try {
        const result = (await props.instance?.execute(command)) as PrmSearchResult;
        if (result && result.items) {
          loadedRows.value = result.items[0].table?.rows || [];
          totalCount.value = result.items[0].table?.rowsTotalCount || 0;
        } else {
          loadedRows.value = [];
          totalCount.value = 0;
        }
        isSearched.value = true;
        isLoading.value = false;
      } catch (error) {
        // Ignore Error
      }
    };

    const loadAutocomplete = async () => {
      if (!filter.value || isAutocompleteDisabled.value === true) {
        isAutocompleteVisible.value = false;
        return;
      }

      isAutocompleteLoading.value = true;

      const command = new GetPrmListCommand({
        page: 1,
        pageSize: 10,
        productId: props.productId,
        filter: filter.value || undefined,
        relationshipType: props.relationship.type,
      });
      try {
        const result = (await props.instance?.execute(command)) as PrmSearchResult;
        if (result && result.items) {
          const rows = result.items[0].table?.rows;
          autocomplete.matches = [];
          if (rows) {
            const values = rows.map((a) => a.values);
            autocomplete.matches = values.map((a) => a['Identifier'][0].text as never);
          }

          autocomplete.totalCount = result.items[0].table?.rowsTotalCount || 0;
          autocomplete.visibleMatchCount = autocomplete.matches.length;
        }

        isAutocompleteVisible.value = autocomplete.matches.length > 0;
        isAutocompleteLoading.value = false;
      } catch (error) {
        // Ignore Error
      }
    };

    const onPageChange = async (p: number) => {
      page.value = p;
      await load();
    };

    const search = _debounce(async (event?: KeyboardEvent) => {
      if (event?.key == 'Enter') {
        return;
      }

      page.value = 1;
      await loadAutocomplete();
      if (!isAutocompleteVisible.value) {
        await load();
      }
    }, 1000);

    const searchNow = async () => {
      isAutocompleteVisible.value = false;
      await load();
    };

    onMounted(async () => {
      filter.value = undefined;

      if (props.relationship && props.relationship.images) {
        productsImagesLoading.value = true;

        const result = await (props.instance as Instance)?.httpService?.loadImagesById(
          props.relationship.images,
        );

        if (result?.items) {
          productsImages.value = result.items;
        }

        productsImagesLoading.value = false;
      }
    });

    const onSearch = async (item: unknown) => {
      filter.value = item as string;
      isAutocompleteVisible.value = false;
      await load();
    };

    return {
      prm,
      table,
      rows,
      filter,
      page,
      pageCount,
      totalCount,
      productsImages,
      productsImagesLoading,
      isLoading,
      isAutocompleteVisible,
      isAutocompleteLoading,
      autocomplete,
      onPageChange,
      onSearch,
      search,
      searchNow,
    };
  },
});
